import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Check, X, Clock, AlertTriangle, Printer, RefreshCw, ChevronLeft, ChevronRight, Bell, BellOff, Volume2 } from 'lucide-react';
import { openPrintWindow } from '@/lib/letterhead';
import DatePickerField from '@/components/DatePickerField';

const TURNI_LABELS = {
  mattina: { label: 'Mattina', orari: '06:00 – 13:00', color: 'bg-amber-100 text-amber-800' },
  pomeriggio: { label: 'Pomeriggio', orari: '14:00 – 21:00', color: 'bg-blue-100 text-blue-800' },
  notte: { label: 'Notte', orari: '22:00 – 05:00', color: 'bg-indigo-100 text-indigo-800' },
};

const DELAY_THRESHOLD_MINUTES = 30;

function isOverdue(orario) {
  const now = new Date();
  const [h, m] = orario.split(':').map(Number);
  const scheduled = new Date();
  scheduled.setHours(h, m, 0, 0);
  return (now - scheduled) > DELAY_THRESHOLD_MINUTES * 60 * 1000;
}

function playAlertSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    // 3 short beeps
    [0, 0.2, 0.4].forEach(delay => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      osc.type = 'sine';
      gain.gain.value = 0.15;
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + 0.12);
    });
  } catch { /* silently fail if AudioContext unavailable */ }
}

export default function SomministrazioniTurno() {
  const { hasRole } = useAuth();
  const [data, setData] = useState(new Date().toISOString().slice(0, 10));
  const [area, setArea] = useState('tutte');
  const [turno, setTurno] = useState(() => {
    const h = new Date().getHours();
    if (h >= 6 && h < 14) return 'mattina';
    if (h >= 14 && h < 22) return 'pomeriggio';
    return 'notte';
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [confirmNote, setConfirmNote] = useState('');
  const [confirmStato, setConfirmStato] = useState('somministrato');
  const [alertEnabled, setAlertEnabled] = useState(true);
  const [overdueCount, setOverdueCount] = useState(0);
  const [lastAlertTime, setLastAlertTime] = useState(0);

  // Calculate isToday before useEffects that depend on it
  const isToday = data === new Date().toISOString().slice(0, 10);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { data, turno };
      if (area !== 'tutte') params.area = area;
      const res = await api.get('/somministrazioni/turno', { params });
      setResult(res.data);
    } catch { toast.error('Errore caricamento dati'); }
    setLoading(false);
  }, [data, area, turno]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Check overdue administrations every 60 seconds
  useEffect(() => {
    if (!result || !isToday) return;
    const checkOverdue = () => {
      const pending = (result?.schedule || []).filter(s => s.stato === 'da_somministrare');
      const overdue = pending.filter(s => isOverdue(s.orario));
      setOverdueCount(overdue.length);
      if (overdue.length > 0 && alertEnabled && Date.now() - lastAlertTime > 120000) {
        playAlertSound();
        setLastAlertTime(Date.now());
      }
    };
    checkOverdue();
    const interval = setInterval(checkOverdue, 60000);
    return () => clearInterval(interval);
  }, [result, alertEnabled, isToday, lastAlertTime]);

  const handleConfirm = async () => {
    if (!confirmDialog) return;
    try {
      await api.post('/somministrazioni', {
        terapia_id: confirmDialog.terapia_id,
        ospite_id: confirmDialog.ospite_id,
        data,
        orario: confirmDialog.orario,
        stato: confirmStato,
        note: confirmNote,
      });
      toast.success(confirmStato === 'somministrato' ? 'Somministrazione registrata' : 'Stato registrato');
      setConfirmDialog(null);
      setConfirmNote('');
      setConfirmStato('somministrato');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const handleQuickConfirm = async (item) => {
    try {
      await api.post('/somministrazioni', {
        terapia_id: item.terapia_id,
        ospite_id: item.ospite_id,
        data,
        orario: item.orario,
        stato: 'somministrato',
        note: '',
      });
      toast.success(`${item.farmaco} somministrato`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const changeDate = (offset) => {
    const d = new Date(data);
    d.setDate(d.getDate() + offset);
    setData(d.toISOString().slice(0, 10));
  };

  const stats = result?.stats || { totale: 0, completate: 0, rifiutate: 0, da_fare: 0 };
  const schedule = result?.schedule || [];
  const pct = stats.totale > 0 ? Math.round((stats.completate / stats.totale) * 100) : 0;

  // Group by orario
  const grouped = {};
  schedule.forEach(item => {
    if (!grouped[item.orario]) grouped[item.orario] = [];
    grouped[item.orario].push(item);
  });
  const sortedOrari = Object.keys(grouped).sort();

  const handlePrint = () => {
    let html = `<p>Turno: <strong>${TURNI_LABELS[turno]?.label || 'Tutti'}</strong> (${TURNI_LABELS[turno]?.orari || ''}) | Area: <strong>${area === 'tutte' ? 'Tutte' : area === 'gialla' ? 'Gialla' : 'Verde'}</strong></p>`;

    sortedOrari.forEach(orario => {
      const items = grouped[orario];
      html += `<div class="section-title">Ore ${orario} (${items.length} somministrazioni)</div>`;
      html += `<table><tr><th>Ospite</th><th>Area</th><th>Farmaco</th><th>Dosaggio</th><th>Via</th><th>Stato</th><th>Firma</th></tr>`;
      items.forEach(item => {
        const stato = item.stato === 'somministrato' ? '<span class="done">OK</span>'
          : item.stato === 'rifiutato' || item.stato === 'non_somministrato' ? `<span class="refused">${item.stato}</span>`
          : '<span class="checkbox"></span>';
        html += `<tr><td>${item.ospite_cognome} ${item.ospite_nome}</td><td>${item.ospite_area === 'verde' ? 'V' : 'G'}</td><td>${item.farmaco}</td><td>${item.dosaggio}</td><td>${item.via || '-'}</td><td>${stato}</td><td></td></tr>`;
      });
      html += `</table>`;
    });

    openPrintWindow('Scheda Somministrazioni', html, {
      subtitle: new Date(data).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    });
  };

  return (
    <div className="space-y-4" data-testid="somministrazioni-turno">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-800" data-testid="page-title">Somministrazioni Turno</h2>
          <p className="text-xs text-slate-500">Vista centralizzata delle somministrazioni per area e fascia oraria</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-turno-btn">
            <Printer className="w-4 h-4 mr-1" />Stampa
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-turno-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Aggiorna
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200">
        {/* Date */}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => changeDate(-1)} data-testid="prev-day">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <DatePickerField value={data} onChange={v => setData(v)} className="w-40" testId="date-filter" />
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => changeDate(1)} data-testid="next-day">
            <ChevronRight className="w-4 h-4" />
          </Button>
          {!isToday && (
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setData(new Date().toISOString().slice(0, 10))} data-testid="today-btn">
              Oggi
            </Button>
          )}
        </div>

        {/* Turno */}
        <div className="flex gap-1">
          {Object.entries(TURNI_LABELS).map(([key, val]) => (
            <button key={key} onClick={() => setTurno(key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${turno === key ? val.color + ' border-transparent shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
              data-testid={`turno-${key}`}>
              {val.label}
            </button>
          ))}
        </div>

        {/* Area */}
        <Select value={area} onValueChange={setArea}>
          <SelectTrigger className="w-36 h-8 text-xs" data-testid="area-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tutte">Tutte le aree</SelectItem>
            <SelectItem value="gialla">Area Gialla</SelectItem>
            <SelectItem value="verde">Area Verde</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Overdue alert banner */}
      {isToday && overdueCount > 0 && (
        <div className="flex items-center justify-between p-3 rounded-xl bg-red-50 border border-red-200 animate-pulse" data-testid="overdue-alert">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center">
              <Bell className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-bold text-red-800">{overdueCount} somministrazion{overdueCount === 1 ? 'e' : 'i'} in ritardo</p>
              <p className="text-xs text-red-600">Superat{overdueCount === 1 ? 'a' : 'e'} da oltre {DELAY_THRESHOLD_MINUTES} minuti senza conferma</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setAlertEnabled(!alertEnabled)}
            className={`h-8 text-xs ${alertEnabled ? 'text-red-600 hover:text-red-700' : 'text-slate-400'}`}
            data-testid="mute-alert-btn">
            {alertEnabled ? <><Volume2 className="w-4 h-4 mr-1" />Silenzia</> : <><BellOff className="w-4 h-4 mr-1" />Silenzioso</>}
          </Button>
        </div>
      )}

      {/* Progress bar */}
      <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm" data-testid="progress-section">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-4">
            <span className="text-2xl font-bold text-slate-800" data-testid="progress-pct">{pct}%</span>
            <div className="flex gap-3 text-xs">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> Completate: <strong>{stats.completate}</strong></span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400"></span> Rifiutate: <strong>{stats.rifiutate}</strong></span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-300"></span> Da fare: <strong>{stats.da_fare}</strong></span>
            </div>
          </div>
          <span className="text-sm text-slate-500">{stats.completate}/{stats.totale}</span>
        </div>
        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500 ease-out"
            style={{
              width: `${pct}%`,
              background: pct === 100 ? '#16a34a' : pct > 70 ? '#22c55e' : pct > 40 ? '#eab308' : '#f97316'
            }}
            data-testid="progress-bar" />
        </div>
      </div>

      {/* Schedule grouped by orario */}
      {sortedOrari.length === 0 ? (
        <div className="text-center py-12 text-slate-400" data-testid="empty-schedule">
          <Clock className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="text-sm">Nessuna somministrazione prevista per questo turno</p>
        </div>
      ) : (
        sortedOrari.map(orario => {
          const items = grouped[orario];
          const completedCount = items.filter(i => i.stato === 'somministrato').length;
          const allDone = completedCount === items.length;
          return (
            <div key={orario} className={`rounded-xl border overflow-hidden ${allDone ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-white'}`}
              data-testid={`orario-group-${orario}`}>
              <div className={`px-4 py-2 flex items-center justify-between ${allDone ? 'bg-emerald-100' : 'bg-slate-50'}`}>
                <div className="flex items-center gap-3">
                  <span className={`text-base font-bold font-mono ${allDone ? 'text-emerald-700' : 'text-slate-700'}`}>{orario}</span>
                  <Badge className={allDone ? 'bg-emerald-200 text-emerald-800' : 'bg-slate-200 text-slate-700'} variant="outline">
                    {completedCount}/{items.length}
                  </Badge>
                </div>
                {allDone && <Check className="w-5 h-5 text-emerald-600" />}
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-48">Ospite</TableHead>
                    <TableHead className="w-16">Area</TableHead>
                    <TableHead>Farmaco</TableHead>
                    <TableHead>Dosaggio</TableHead>
                    <TableHead className="w-24">Via</TableHead>
                    <TableHead className="w-28">Stato</TableHead>
                    {hasRole('infermiere', 'coordinatore', 'medico') && <TableHead className="w-36"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item, idx) => (
                    <TableRow key={`${item.terapia_id}_${item.orario}_${idx}`}
                      className={item.stato === 'somministrato' ? 'bg-emerald-50/50' : item.stato === 'rifiutato' || item.stato === 'non_somministrato' ? 'bg-red-50/50' : isToday && item.stato === 'da_somministrare' && isOverdue(item.orario) ? 'bg-red-50/30 border-l-2 border-l-red-400' : ''}
                      data-testid={`somm-row-${item.terapia_id}-${item.orario}`}>
                      <TableCell className="font-medium text-sm">
                        {item.ospite_cognome} {item.ospite_nome}
                        {item.ospite_stanza && <span className="block text-[10px] text-slate-400">Camera {item.ospite_stanza}</span>}
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-[10px] ${item.ospite_area === 'verde' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {item.ospite_area === 'verde' ? 'Verde' : 'Gialla'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.farmaco}
                        {item.principio_attivo && <span className="block text-[10px] text-slate-400">{item.principio_attivo}</span>}
                      </TableCell>
                      <TableCell className="text-sm font-medium">{item.dosaggio}</TableCell>
                      <TableCell className="text-xs capitalize">{item.via || '-'}</TableCell>
                      <TableCell>
                        {item.stato === 'somministrato' ? (
                          <div>
                            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]"><Check className="w-3 h-3 mr-0.5" />Fatto</Badge>
                            {item.operatore && <span className="block text-[9px] text-slate-400 mt-0.5">{item.operatore}</span>}
                          </div>
                        ) : item.stato === 'rifiutato' || item.stato === 'non_somministrato' ? (
                          <div>
                            <Badge className="bg-red-100 text-red-700 text-[10px]"><X className="w-3 h-3 mr-0.5" />{item.stato === 'rifiutato' ? 'Rifiutato' : 'Non somm.'}</Badge>
                            {item.note_somm && <span className="block text-[9px] text-red-400 mt-0.5">{item.note_somm}</span>}
                          </div>
                        ) : (
                          isToday && isOverdue(item.orario) ? (
                            <Badge className="bg-red-100 text-red-700 text-[10px] animate-pulse" data-testid="overdue-badge"><AlertTriangle className="w-3 h-3 mr-0.5" />In ritardo!</Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-500 text-[10px]"><Clock className="w-3 h-3 mr-0.5" />In attesa</Badge>
                          )
                        )}
                      </TableCell>
                      {hasRole('infermiere', 'coordinatore', 'medico') && (
                        <TableCell>
                          {item.stato === 'da_somministrare' && (
                            <div className="flex gap-1">
                              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                                onClick={() => handleQuickConfirm(item)} data-testid={`quick-confirm-${item.terapia_id}-${item.orario}`}>
                                <Check className="w-3.5 h-3.5 mr-0.5" />OK
                              </Button>
                              <Button variant="outline" size="sm" className="h-7 text-xs text-slate-500"
                                onClick={() => { setConfirmDialog(item); setConfirmStato('somministrato'); setConfirmNote(''); }}
                                data-testid={`detail-confirm-${item.terapia_id}-${item.orario}`}>
                                <AlertTriangle className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          );
        })
      )}

      {/* Confirm dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={(v) => { if (!v) setConfirmDialog(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">Registra Somministrazione</DialogTitle>
          </DialogHeader>
          {confirmDialog && (
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-slate-50 border text-sm">
                <p className="font-bold">{confirmDialog.ospite_cognome} {confirmDialog.ospite_nome}</p>
                <p className="text-slate-600">{confirmDialog.farmaco} — {confirmDialog.dosaggio}</p>
                <p className="text-xs text-slate-400">Ore {confirmDialog.orario}</p>
              </div>
              <div>
                <Label className="text-xs">Stato</Label>
                <Select value={confirmStato} onValueChange={setConfirmStato}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="somministrato">Somministrato</SelectItem>
                    <SelectItem value="rifiutato">Rifiutato dall'ospite</SelectItem>
                    <SelectItem value="non_somministrato">Non somministrato</SelectItem>
                    <SelectItem value="non_disponibile">Farmaco non disponibile</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Note</Label>
                <Input value={confirmNote} onChange={e => setConfirmNote(e.target.value)} placeholder="Note opzionali..." data-testid="confirm-note" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConfirmDialog(null)}>Annulla</Button>
                <Button onClick={handleConfirm}
                  className={confirmStato === 'somministrato' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
                  data-testid="confirm-somm-btn">
                  Conferma
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
